import Button from '@mui/material/Button'

import type { OutputRecipientList, OutputListSortableField } from 'common/api/v1/types'
import { useConfirmationDialog, useOutputListsSelector, usePageParamsFilteredSelector } from '../../utils'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { Link } from '../common/Link'
import { AppDispatch, useRoutes } from '../../store'
import type { EnrichedOutputRecipientList } from '../../api/nm-types'
import { UrlParamFilteredSearchBar } from '../common/Filters/FilterView/FilteredSearchBar'
import Toolbar from '@mui/material/Toolbar'
import {
  ListOutputListFilterKey,
  makeListOutputListsFilter,
  mapOutputListFilterToUrlParam,
  mapUrlParamToOutputListFilter,
} from './listOutputListsFilter'
import { ActionMenu } from '../common/Table/ActionMenu'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsIcon from '@mui/icons-material/Settings'
import { useDispatch } from 'react-redux'
import { removeOutputList } from '../../redux/actions/outputListsActions'

export const ListView = () => {
  const routes = useRoutes()
  const { outputLists, total, loading } = usePageParamsFilteredSelector(useOutputListsSelector)
  const showConfirmation = useConfirmationDialog()
  const dispatch = useDispatch<AppDispatch>()

  const tableConfig: TableConfig<EnrichedOutputRecipientList, OutputListSortableField> = [
    {
      title: 'name',
      getValue: (outputList) => (
        <Link to={routes.outputListsUpdate({ id: outputList.id })} underline="hover">
          {outputList.name}
        </Link>
      ),
      sorting: { byParameter: 'name' },
    },
    {
      title: 'description',
      getValue: ({ description }) => description,
      sorting: { byParameter: 'description' },
    },
    {
      title: 'group',
      getValue: (groupList) => (
        <Link to={routes.groupsUpdate({ id: groupList.group })} underline="hover">
          {groupList._group?.name}
        </Link>
      ),
    },
    {
      getValue: (outputList) => (
        <ActionMenu
          options={[
            {
              label: 'Edit',
              icon: <SettingsIcon />,
              href: routes.outputListsUpdate({ id: outputList.id }),
            },
            {
              label: 'Remove',
              icon: <DeleteIcon />,
              isDestructive: true,
              onClick: () => {
                showConfirmation(
                  () => dispatch(removeOutputList(outputList.id)),
                  `Are you sure you want to remove output list ${outputList.name}?`,
                )
              },
            },
          ]}
        />
      ),
      props: { align: 'right' },
    },
  ]
  const filters = makeListOutputListsFilter()
  return (
    <Wrapper
      name={['Outputs', 'Lists']}
      actions={
        <Link key="outputListCreate" type="button" to={routes.outputListsNew()}>
          <Button variant="contained" color="primary">
            Add output list
          </Button>
        </Link>
      }
    >
      <Toolbar disableGutters>
        <div style={{ flexShrink: 1, flexGrow: 1 }}>
          <UrlParamFilteredSearchBar
            urlParamCacheKey={'outputLists'}
            mapUrlParamToFilterFn={mapUrlParamToOutputListFilter}
            mapFilterToUrlParamFn={mapOutputListFilterToUrlParam}
            searchTokenFilters={filters}
            rawTextFilter={filters.find((f) => f.key === ListOutputListFilterKey.name)!}
          />
        </div>
      </Toolbar>
      <Table<OutputRecipientList>
        id="output-lists-table"
        emptyMessageComponent={
          <MissingContent message="No output lists" buttonText="Add output list" url={routes.outputListsNew()} />
        }
        config={tableConfig}
        data={outputLists}
        pending={loading}
        pagination={{ total, useUrlSearchParams: true }}
      />
    </Wrapper>
  )
}
